import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const colLeftVariants = {
  hidden: { opacity: 0, x: -30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const colRightVariants = {
  hidden: { opacity: 0, x: 30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const infoContainerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.13, delayChildren: 0.2 } },
};

const infoItemVariants = {
  hidden: { opacity: 0, y: 24, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function ContactPage() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    privacy: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = t('contact.form.required');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('contact.form.required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.form.invalidEmail');
    }

    if (!formData.message.trim()) {
      newErrors.message = t('contact.form.required');
    } else if (formData.message.length > 500) {
      newErrors.message = t('contact.form.maxLength');
    }

    if (!formData.privacy) {
      newErrors.privacy = t('contact.form.required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const formBody = new URLSearchParams();
      formBody.append('fullName', formData.fullName);
      formBody.append('email', formData.email);
      formBody.append('company', formData.company);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6kgvl05dsf7sr1n5fn0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          email: '',
          company: '',
          phone: '',
          message: '',
          privacy: false
        });
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 5000);
      }
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f1419] flex flex-col">
      <Header />
      
      <main className="flex-1 py-28 lg:py-36">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left Column - Contact Info */}
            <motion.div
              variants={colLeftVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
            >
              <h1 className="text-white font-bold text-4xl md:text-5xl mb-4">
                {t('contact.title')}
              </h1>
              <p className="text-[rgba(255,255,255,0.6)] text-lg mb-12">
                {t('contact.subtitle')}
              </p>

              <motion.div
                className="flex flex-col gap-4"
                variants={infoContainerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
              >
                {/* Address */}
                <motion.div
                  variants={infoItemVariants}
                  className="p-6 rounded-xl bg-[rgba(255,255,255,0.08)] backdrop-blur-md hover:bg-[rgba(255,255,255,0.12)] transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-[#00d4ff] text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-white text-base font-semibold mb-2 whitespace-nowrap">
                        {t('contact.address')}
                      </h3>
                      <p className="text-[rgba(255,255,255,0.7)] text-sm leading-relaxed">
                        Kaplanova 2252/8<br />
                        Chodov<br />
                        148 00 Praha 4<br />
                        Czech Republic
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Phone */}
                <motion.a
                  variants={infoItemVariants}
                  href="tel:+420720463376"
                  className="p-6 rounded-xl bg-[rgba(255,255,255,0.08)] backdrop-blur-md hover:bg-[rgba(255,255,255,0.12)] hover:shadow-[0_4px_20px_rgba(0,212,255,0.2)] transition-all duration-300 cursor-pointer"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-[#00d4ff] text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-white text-base font-semibold mb-1 whitespace-nowrap">
                        {t('contact.phone')}
                      </h3>
                      <p className="text-[rgba(255,255,255,0.7)] text-sm whitespace-nowrap">
                        +420 720 463 376
                      </p>
                    </div>
                  </div>
                </motion.a>

                {/* Email */}
                <motion.a
                  variants={infoItemVariants}
                  href="mailto:nexnovasolution@gmail.com"
                  className="p-6 rounded-xl bg-[rgba(255,255,255,0.08)] backdrop-blur-md hover:bg-[rgba(255,255,255,0.12)] hover:shadow-[0_4px_20px_rgba(0,212,255,0.2)] transition-all duration-300 cursor-pointer"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-[#00d4ff] text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-white text-base font-semibold mb-1 whitespace-nowrap">
                        {t('contact.email')}
                      </h3>
                      <p className="text-[rgba(255,255,255,0.7)] text-sm break-all">
                        nexnovasolution@gmail.com
                      </p>
                    </div>
                  </div>
                </motion.a>
              </motion.div>
            </motion.div>

            {/* Right Column - Contact Form */}
            <motion.div
              variants={colRightVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
            >
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="p-12 rounded-[20px] bg-[rgba(255,255,255,0.1)] backdrop-blur-xl border border-[rgba(255,255,255,0.15)]"
              >
                {/* Full Name */}
                <div className="mb-6">
                  <label className="block text-[rgba(255,255,255,0.8)] text-sm font-medium mb-2">
                    {t('contact.form.fullName')} *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    className="w-full h-[52px] px-4 rounded-xl bg-[#1a1d23] text-white border border-[rgba(255,255,255,0.2)] focus:border-[#00d4ff] focus:outline-none transition-colors duration-300"
                  />
                  {errors.fullName && (
                    <p className="text-red-400 text-xs mt-1">{errors.fullName}</p>
                  )}
                </div>

                {/* Email */}
                <div className="mb-6">
                  <label className="block text-[rgba(255,255,255,0.8)] text-sm font-medium mb-2">
                    {t('contact.form.emailLabel')} *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full h-[52px] px-4 rounded-xl bg-[#1a1d23] text-white border border-[rgba(255,255,255,0.2)] focus:border-[#00d4ff] focus:outline-none transition-colors duration-300"
                  />
                  {errors.email && (
                    <p className="text-red-400 text-xs mt-1">{errors.email}</p>
                  )}
                </div>

                {/* Company */}
                <div className="mb-6">
                  <label className="block text-[rgba(255,255,255,0.8)] text-sm font-medium mb-2">
                    {t('contact.form.company')}
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full h-[52px] px-4 rounded-xl bg-[#1a1d23] text-white border border-[rgba(255,255,255,0.2)] focus:border-[#00d4ff] focus:outline-none transition-colors duration-300"
                  />
                </div>

                {/* Phone */}
                <div className="mb-6">
                  <label className="block text-[rgba(255,255,255,0.8)] text-sm font-medium mb-2">
                    {t('contact.form.phoneLabel')}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full h-[52px] px-4 rounded-xl bg-[#1a1d23] text-white border border-[rgba(255,255,255,0.2)] focus:border-[#00d4ff] focus:outline-none transition-colors duration-300"
                  />
                </div>

                {/* Message */}
                <div className="mb-6">
                  <label className="block text-[rgba(255,255,255,0.8)] text-sm font-medium mb-2">
                    {t('contact.form.message')} *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl bg-[#1a1d23] text-white border border-[rgba(255,255,255,0.2)] focus:border-[#00d4ff] focus:outline-none transition-colors duration-300 resize-vertical"
                  ></textarea>
                  <div className="flex justify-between items-center mt-1">
                    {errors.message && (
                      <p className="text-red-400 text-xs">{errors.message}</p>
                    )}
                    <p className="text-[rgba(255,255,255,0.4)] text-xs ml-auto">
                      {formData.message.length}/500
                    </p>
                  </div>
                </div>

                {/* Privacy Checkbox */}
                <div className="mb-8">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="privacy"
                      checked={formData.privacy}
                      onChange={(e) => setFormData({ ...formData, privacy: e.target.checked })}
                      className="w-5 h-5 mt-0.5 rounded border-[#00d4ff] text-[#00d4ff] focus:ring-[#00d4ff] focus:ring-offset-0 bg-[#1a1d23] cursor-pointer"
                    />
                    <span className="text-[rgba(255,255,255,0.7)] text-xs leading-relaxed">
                      {t('contact.form.privacy')}
                    </span>
                  </label>
                  {errors.privacy && (
                    <p className="text-red-400 text-xs mt-1">{errors.privacy}</p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="w-full h-14 rounded-xl bg-[#00d4ff] text-[#0f1419] font-semibold text-base hover:bg-[#00bfe6] hover:shadow-[0_4px_20px_rgba(0,212,255,0.4)] transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  {t('contact.form.submit')}
                  <i className="ri-send-plane-fill text-lg"></i>
                </button>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="mt-4 p-4 rounded-lg bg-green-500/20 border border-green-500/50">
                    <p className="text-green-400 text-sm text-center">
                      {t('contact.form.success')}
                    </p>
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="mt-4 p-4 rounded-lg bg-red-500/20 border border-red-500/50">
                    <p className="text-red-400 text-sm text-center">
                      {t('contact.form.error')}
                    </p>
                  </div>
                )}
              </form>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}